import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import _ from 'underscore';
import uuid from 'uuid/v1';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import {
	Card,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
	Tooltip,
	Divider,
	CardActions,
	colors,
	Grid,
	Button,
} from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import DatePicker from 'components/DatePicker/DatePicker.js';
import { SelectField, Checkbox } from 'components';

import { GenerateReport, SetSelectedSites } from '../../../actions/ReportsActions.js';

const useStyles = makeStyles((theme) => ({
	root: {},
	value: {
		fontWeight: theme.typography.fontWeightMedium,
		align: 'center'
	},
	type: {
		fontWeight: theme.typography.fontWeightMedium,
	},
	warning: {
		color: colors.orange[500]
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const reportTypes = [
	{ value: 'hourly24', text: '24 Hours' },
	{ value: 'hourly48', text: '48 Hours' },
	{ value: 'daily7', text: '7 Days' },
	{ value: 'daily7ac', text: '7 Days AC' },
];

const SiteList = (props) => {
	const { className, ...rest } = props;
	const dispatch = useDispatch();
	const classes = useStyles();
	const siteList = useSelector((state) => state.reportsReducer.selectedSites);
	const user = useSelector((state) => state.session.user);

	const _generate = () => {
		const username = `${user.firstname} ${user.lastname}`;
		dispatch(GenerateReport(siteList, username, user.id));
	};

	const _checkWarning = (site) => {
		if (site.scheduleStatus === 'Complete' && _.isUndefined(site.commissioningDate)) {
			return <WarningRoundedIcon className={classes.warning} />;
		}
		if (site.scheduleStatus !== 'Complete' && moment(site.scheduleDate).format('YYYY-MM-DD') < moment().format('YYYY-MM-DD')) {
			return <WarningRoundedIcon className={classes.warning} />;
		}
		if (site.scheduleDate
			&& site.commissioningDate
			&& moment(site.scheduleDate).format('YYYY-MM-DD') !== moment(site.commissioningDate).format('YYYY-MM-DD')) {
			return <WarningRoundedIcon className={classes.warning} />;
		}

		return null;
	};

	const _dateChange = (value, site, type) => {
		const newSite = _.findWhere(siteList, { siteId: site.siteId, sow: site.sow });
		newSite[type] = value.format('YYYY-MM-DD');
		// Find item index using _.findIndex (thanks @AJ Richardson for comment)
		const index = _.findIndex(siteList, { siteId: site.siteId, sow: site.sow });

		// Replace item at index using native splice
		const newSiteList = [...siteList];
		newSiteList.splice(index, 1, newSite);
		dispatch(SetSelectedSites(newSiteList));
	};

	const _checkboxChange = (value, site, type) => {
		const newSite = _.findWhere(siteList, { siteId: site.siteId, sow: site.sow });
		newSite[type] = value;
		// Find item index using _.findIndex (thanks @AJ Richardson for comment)
		const index = _.findIndex(siteList, { siteId: site.siteId, sow: site.sow });

		// Replace item at index using native splice
		const newSiteList = [...siteList];
		newSiteList.splice(index, 1, newSite);
		dispatch(SetSelectedSites(newSiteList));
	};

	const _handleChange = (event, site) => {
		const newSite = _.findWhere(siteList, { siteId: site.siteId, sow: site.sow });
		newSite.reportType = event.target.value;
		if (event.target.value !== 'hourly24') {
			newSite.bis = false;
		}
		// Find item index using _.findIndex (thanks @AJ Richardson for comment)
		const index = _.findIndex(siteList, { siteId: site.siteId, sow: site.sow });

		// Replace item at index using native splice
		const newSiteList = [...siteList];
		newSiteList.splice(index, 1, newSite);
		dispatch(SetSelectedSites(newSiteList));
	};

	const _getDefaultReportType = (site) => {
		const diff = moment().diff(moment(site.commissioningDate), 'hours');
		let type = 'daily7';
		if (diff < 48) {
			type = 'hourly24';
		}
		if (diff >= 48 && diff < 168) {
			type = 'hourly48';
		}

		const newSite = _.findWhere(siteList, { siteId: site.siteId, sow: site.sow });
		newSite.reportType = type;
		// Find item index using _.findIndex (thanks @AJ Richardson for comment)
		const index = _.findIndex(siteList, { siteId: site.siteId, sow: site.sow });

		// Replace item at index using native splice
		const newSiteList = [...siteList];
		newSiteList.splice(index, 1, newSite);
		dispatch(SetSelectedSites(newSiteList));
		return type;
	};

	return (
		<Card {...rest} className={clsx(classes.root, className)}>
			<List>
				{siteList.map((site, i) => (
					<ListItem
						divider={i < siteList.length - 1}
						key={site.id}
					>
						<ListItemIcon>
							{_checkWarning(site)}
						</ListItemIcon>
						<Grid container spacing={1}>
							<Grid item md={1} xs={12}>
								<ListItemText
									primary={(
										<>
											<Typography
												component="span"
												color="textPrimary"
												style={{ fontWeight: 'bold' }}
											>
												{site.siteId}
											</Typography>
										</>
									)}
									secondary={(
										<>
											<Typography
												component="span"
												variant="body2"
												color="textPrimary"
											>
												{site.siteName}
											</Typography>
										</>
									)}
								/>
							</Grid>
							<Grid item md={1} xs={12}>
								<ListItemText
									primary={(
										<>
											<Typography
												component="span"
												color="textPrimary"
												style={{ fontWeight: 'bold' }}
											>
												{site.scheduleStatus}
											</Typography>
										</>
									)}
									secondary={(
										<>
											<Typography
												component="span"
												variant="body2"
												color="textPrimary"
											>
												Schedule Status
											</Typography>
										</>
									)}
								/>
							</Grid>
							<Grid item md={1} xs={12}>
								<ListItemText
									primary={(
										<>
											<Typography
												component="span"
												color="textPrimary"
												style={{ fontWeight: 'bold' }}
											>
												{site.actionStatus}
											</Typography>
										</>
									)}
									secondary={(
										<>
											<Typography
												component="span"
												variant="body2"
												color="textPrimary"
											>
												Action Status
											</Typography>
										</>
									)}
								/>
							</Grid>
							<Grid item md={1} xs={12}>
								<ListItemText
									primary={(
										<>
											<Typography
												component="span"
												color="textPrimary"
												style={{ fontWeight: 'bold' }}
											>
												{site.sow}
											</Typography>
										</>
									)}
									secondary={(
										<>
											<Typography
												component="span"
												variant="body2"
												color="textPrimary"
											>
												SOW
											</Typography>
										</>
									)}
								/>
							</Grid>
							<Grid item md={2} xs={12}>
								<DatePicker
									label="Schedule Date"
									value={site.scheduleDate && moment(site.scheduleDate).format('YYYY-MM-DD')}
									onChange={(value) => _dateChange(value, site, 'scheduleDate')}
								/>
							</Grid>
							<Grid item md={2} xs={12}>
								<DatePicker
									label="Commissioning Date"
									value={site.commissioningDate && moment(site.commissioningDate).format('YYYY-MM-DD')}
									onChange={(value) => _dateChange(value, site, 'commissioningDate')}
								/>
							</Grid>
							<Grid item md={2} xs={12}>
								<SelectField
									id="reportType"
									name="reportType"
									value={site.reportType || _getDefaultReportType(site)}
									onChange={(value) => _handleChange(value, site)}
									hintText="Report Type"
									menuItems={reportTypes}
								/>
							</Grid>
							<Grid item md={1} xs={12}>
								<Checkbox
									label="BIS Template"
									checked={site.bis && site.reportType === 'hourly24'}
									disabled={site.reportType !== 'hourly24'}
									onCheck={(event) => _checkboxChange(event.target.checked, site, 'bis')}
								/>
							</Grid>
							<Grid item md={1} xs={12}>
								<Checkbox
									label="Disable D_DAY"
									checked={site.disableDDay}
									onCheck={(event) => _checkboxChange(event.target.checked, site, 'disableDDay')}
								/>
							</Grid>
						</Grid>
					</ListItem>
				))}
			</List>
			<Divider />
			<CardActions>
				<Button className={classes.generate} onClick={_generate} variant="contained">Generate</Button>
			</CardActions>
		</Card>
	);
};

SiteList.propTypes = {
	className: PropTypes.string
};

export default SiteList;
